import type { Currency, CurrencyFormat } from '@/types';

// Pure conversion/rounding/formatting core. No I/O, no React, no Supabase —
// shared verbatim by the client (useCurrency) and the server (order snapshot)
// so the amount a customer sees is byte-identical to the amount stored.

// Safety net for renders that happen before the currency config loads (and for
// the build phase, where the DB is unreachable). Matches the seeded USD row.
export const USD_FALLBACK: Currency = {
    id: 'usd-fallback',
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    rateToUsd: 1,
    decimals: 2,
    roundTo: 0.01,
    isEnabled: true,
    isDefault: true,
    isBase: true,
    position: 0,
    createdAt: '',
    updatedAt: '',
};

// Snaps a value to the nearest multiple of roundTo (5000 for LBP, 0.01 for USD).
// The toFixed pass strips float noise like 1119999.9999999999.
export function roundToNearest(value: number, roundTo: number): number {
    if (roundTo <= 0) return value;
    return parseFloat((Math.round(value / roundTo) * roundTo).toFixed(6));
}

export function convertFromUsd(usdAmount: number, currency: CurrencyFormat): number {
    return roundToNearest(usdAmount * currency.rateToUsd, currency.roundTo);
}

// Formats an amount already denominated in the given currency: "$12.50",
// "LL 1,120,000", "-$3.00". Multi-character symbols get a separating space.
export function formatAmount(amount: number, currency: CurrencyFormat): string {
    const sign = amount < 0 ? '-' : '';
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: currency.decimals,
        maximumFractionDigits: currency.decimals,
    }).format(Math.abs(amount));
    const separator = currency.symbol.length > 1 ? ' ' : '';
    return `${sign}${currency.symbol}${separator}${formatted}`;
}

// The one call sites use: USD in, display string out.
export function formatUsd(usdAmount: number, currency: CurrencyFormat): string {
    return formatAmount(convertFromUsd(usdAmount, currency), currency);
}
